/**
 * The words of the three invitation-code panels (the account question, the outcome, the progress), as plain data.
 *
 * ONE copy, read by today's panels (./RedemptionPanels.tsx, for /pricing and /start) and by the pricing page's rebuild in
 * the new design, which may not import a styled-components module (design-system §7b). Moved here on 2026-09-30; the
 * words are unchanged. Copy is lowercase by convention (the wallet and portal style); the icons are decoration, drawn
 * aria-hidden by the new design.
 */
import { usdToWu, formatWu, WU_LABEL } from '../../config/workUnits';

/** The shape of a redeemed code's result (the redeem function's answer, read by useAccessCodeRedemption). */
export interface RedemptionOutcome {
  mode: 'wallet_credit' | 'trial_extension';
  creditCents: number;
  expiresAt?: string | null;
}

/** "$25" for whole dollars, "$12.50" otherwise. */
export function formatCreditDollars(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars.toLocaleString()}` : `$${dollars.toFixed(2)}`;
}

/** Signed out with a valid unused code: which account should it apply to? */
export const ACCOUNT_QUESTION_WORDS = {
  icon: '👋',
  title: 'quick question — do you already have a prest0 account?',
  body:
    "if you do, sign in first so your code applies to the right account. if you're new here, we'll get you set up in under a minute.",
  signIn: 'yes — sign me in',
  createAccount: 'no — create my account',
} as const;

export interface OutcomeWords {
  icon: string;
  title: string;
  body: string;
  /** The first action, and where it goes. */
  primary: { label: string; to: 'wallet' | 'workspace' };
  /** The second action, when there is one. */
  secondary?: { label: string; to: 'wallet' | 'workspace' };
}

/** What a signed-in redemption says: credit to the wallet, or a longer trial. */
export function outcomeWords(outcome: RedemptionOutcome): OutcomeWords {
  const credit = formatCreditDollars(outcome.creditCents);
  if (outcome.mode === 'wallet_credit') {
    // Display-only WU conversion at the Standard Rate; the credited amount itself (cents) is unchanged.
    const creditWu = formatWu(usdToWu(outcome.creditCents / 100));
    return {
      icon: '🎉',
      title: 'credit applied',
      body: `${credit} in platform credit has been added to your account as Work Units (≈ ${creditWu} ${WU_LABEL}) — it's yours to use whenever you like.`,
      primary: { label: 'view your wallet', to: 'wallet' },
      secondary: { label: 'back to your workspace', to: 'workspace' },
    };
  }
  const endsOn = outcome.expiresAt
    ? new Date(outcome.expiresAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;
  return {
    icon: '🎉',
    title: 'your trial just grew',
    body: `we've added ${credit} in credit to your trial${endsOn ? ` and extended it through ${endsOn}` : ' and extended your time'} — pick up right where you left off.`,
    primary: { label: 'back to your workspace', to: 'workspace' },
  };
}

export type RedeemProgressStage = 'checking' | 'applying' | 'applied';

/** The progress panel's title and sentence for each stage. */
export const PROGRESS_WORDS: Record<RedeemProgressStage, { title: string; body: string }> = {
  checking: { title: 'checking your invite code…', body: 'one moment — verifying your invitation.' },
  applying: { title: 'applying your invite code…', body: 'hold tight — securely linking your code to your account.' },
  applied: { title: 'code applied', body: 'taking you to your workspace setup…' },
};
